import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { MainMenuFragmentFragment } from '../../../graphql/gatsby'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import { filterMenuEntriesToDisplay } from '../../../utils/sanity/documents/menuEntry'
import { LinkRef } from '../Link'
import { MegaMenuColumn } from './MegaMenuColumn'

type MainMenuEntry = NonNullable<NonNullable<MainMenuFragmentFragment['mainMenu']>[number]>
type SubMenuEntries = NonNullable<MainMenuEntry['childEntries']>

const splitSubMenuIntoColumns = (submenu: SubMenuEntries): SubMenuEntries[] => {
  const columns: SubMenuEntries[] = []
  if (submenu && submenu.length) {
    let sliceBegin = 0
    let i = 0
    for (; i < submenu.length; i++) {
      const subMenuEntry = submenu[i]
      // if newColumn is specified and not the first entry, add slice
      if (subMenuEntry?.newColumn && i > 0) {
        columns.push(submenu.slice(sliceBegin, i))
        // start next slice
        sliceBegin = i
      }
    }
    // add remaining slice to columns array
    columns.push(submenu.slice(sliceBegin, i))
  }
  return columns
}

export interface MegaMenuProps {
  className?: string
  mainMenuEntry: MainMenuEntry
  isVisible: boolean
  menuEntryRef: React.RefObject<LinkRef>
}

const MegaMenu: React.VFC<MegaMenuProps> = ({
  className,
  isVisible,
  mainMenuEntry: menuEntry,
  menuEntryRef,
}) => {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const subMenuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntry.childEntries),
    [menuEntry.childEntries, shouldDisplayContent],
  )
  const columns = useMemo(
    () => (subMenuEntries ? splitSubMenuIntoColumns(subMenuEntries) : []),
    [subMenuEntries],
  )

  const menuWidth = useRef(0)
  const menuLeftOffset = useRef(0)
  // use ref for collection of column widths to be summed
  const columnWidths = useRef<number[]>([])
  // initialize collection of column widths to zero
  columnWidths.current = columns.map((_) => 0)

  const [combinedColumnWidth, setCombinedColumnWidth] = useState(0)

  // callback for setting column width
  const setColumnWidth = (width: number, margin: number, index: number): void => {
    // include margin in width unless first column since that's the margin to be set
    columnWidths.current[index] = index > 0 ? width + margin : width
    // all column widths are set when none are equal to 0
    const areAllWidthsSet = !columnWidths.current.find((columnWidth) => columnWidth === 0)
    if (areAllWidthsSet) {
      // when all widths are set, set the combined column width
      const combined = columnWidths.current.reduce((prev, cur) => prev + cur, 0)
      setCombinedColumnWidth(combined)
    }
  }

  const getMenuEntryOffset = () => (menuEntryRef.current ? menuEntryRef.current.offsetLeft : 0)

  const getOffsetToUse = () => {
    const menuEntryOffset = getMenuEntryOffset()
    const alignedOffset = menuEntryOffset - menuLeftOffset.current
    if (alignedOffset + combinedColumnWidth > menuWidth.current) {
      return menuWidth.current - combinedColumnWidth
    }
    return alignedOffset
  }

  const registerColumnWidth =
    (columnIndex: number): React.Ref<HTMLDivElement> =>
    (el) => {
      if (!el || typeof window === 'undefined') {
        return
      }
      // get margins from computed style
      const style = window.getComputedStyle(el)
      setColumnWidth(
        el.getBoundingClientRect().width,
        parseInt(style.getPropertyValue('margin-left'), 10) +
          parseInt(style.getPropertyValue('margin-right'), 10),
        columnIndex,
      )
    }

  return columns && isVisible ? (
    <div
      className={className}
      ref={(el) => {
        if (el !== null) {
          menuWidth.current = el.getBoundingClientRect().width
          menuLeftOffset.current = el.offsetLeft
        }
      }}
    >
      {columns.map((entries, columnIndex) => (
        <MegaMenuColumn
          entries={entries}
          key={columnIndex}
          leftOffset={columnIndex === 0 ? getOffsetToUse() : 0}
          seeAllEntry={columnIndex === 0 && menuEntry.link ? menuEntry : null}
          ref={registerColumnWidth(columnIndex)}
        />
      ))}
    </div>
  ) : null
}

const StyledMegaMenu = styled(MegaMenu)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.component.backgroundDarkBlue}F2;
  position: absolute;
  left: 0;
  z-index: -1; // allow bottom border on .nav-item to render above mega menu
  padding: 2rem 0 0 0;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  overflow: auto;
  max-height: 80vh;
  cursor: default; // override styles inherited from .nav-item
  transition:
    opacity 0.35s,
    visibility 0.35s ease-in-out;
  box-shadow: 5px 5px 10px 0 rgba(0, 0, 0, 0.25);
  margin-top: 10px;
  @media (min-width: 1200px) {
    margin-top: 12px;
  }
`

export default StyledMegaMenu
